import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Stack,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import { useVoyagerStore } from '../../stores/useVoyagerStore';
import { getCurrentUser, setCurrentUser } from '../../utils';
import { FilterGroup } from '../../types';

/** Count total conditions in a nested FilterGroup tree. */
function countConditions(group: FilterGroup): number {
  let count = 0;
  for (const child of group.children) {
    if (child.type === 'condition') {
      count += 1;
    } else if (child.type === 'group') {
      count += countConditions(child);
    }
  }
  return count;
}

interface SaveModalProps {
  open: boolean;
  onClose: () => void;
}

const SaveModal: React.FC<SaveModalProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [username, setUsername] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  const selectedMetrics = useVoyagerStore((s) => s.selectedMetrics);
  const selectedDimensions = useVoyagerStore((s) => s.selectedDimensions);
  const filterGroup = useVoyagerStore((s) => s.filterGroup);
  const saveReport = useVoyagerStore((s) => s.saveReport);
  const overwriteReport = useVoyagerStore((s) => s.overwriteReport);
  const loadedReportId = useVoyagerStore((s) => s.loadedReportId);
  const loadedReportName = useVoyagerStore((s) => s.loadedReportName);

  const isLoadedReport = !!loadedReportId;

  useEffect(() => {
    if (open) {
      const existing = getCurrentUser();
      if (existing) {
        setUsername(existing);
        setIsFirstTime(false);
      } else {
        setUsername('');
        setIsFirstTime(true);
      }

      // Reset template state
      setIsTemplate(false);
      setTemplateTags([]);
      setTagInput('');

      // Pre-fill name/description from loaded report
      if (loadedReportId && loadedReportName) {
        setName(loadedReportName);
        // Find the report in the list for description
        const reports = useVoyagerStore.getState().reports;
        const existing = reports.find((r) => r.id === loadedReportId);
        setDescription(existing?.description || '');
      } else {
        setName('');
        setDescription('');
      }
    }
  }, [open, loadedReportId, loadedReportName]);

  const filterCount = countConditions(filterGroup);

  const validate = (): boolean => {
    let hasError = false;
    if (!name.trim()) {
      setNameError(true);
      hasError = true;
    }
    if (isFirstTime && !username.trim()) {
      setUsernameError(true);
      hasError = true;
    }
    return !hasError;
  };

  const persistUsername = () => {
    if (isFirstTime && username.trim()) {
      setCurrentUser(username.trim());
    }
  };

  const templateOptions = isTemplate ? { isTemplate: true, templateTags } : undefined;

  const handleOverwrite = () => {
    if (!validate() || !loadedReportId) return;
    persistUsername();
    overwriteReport(loadedReportId, name.trim(), description.trim(), templateOptions);
    resetAndClose();
  };

  const handleSaveAsNew = () => {
    if (!validate()) return;
    persistUsername();
    saveReport(name.trim(), description.trim(), templateOptions);
    resetAndClose();
  };

  const resetAndClose = () => {
    setName('');
    setDescription('');
    setNameError(false);
    setUsernameError(false);
    onClose();
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setNameError(false);
    setUsernameError(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Save Report
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoadedReport && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You loaded report "<strong>{loadedReportName}</strong>". You can overwrite it or save as a new report.
          </Alert>
        )}

        <TextField
          label="Author"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            if (e.target.value.trim()) setUsernameError(false);
          }}
          fullWidth
          required={isFirstTime}
          disabled={!isFirstTime}
          error={usernameError}
          helperText={
            usernameError
              ? 'Author is required'
              : !isFirstTime
                ? 'Saved from your previous session'
                : 'Enter your name (saved for future use)'
          }
          sx={{ mb: 2 }}
        />

        <TextField
          label="Report Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
          fullWidth
          required
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          sx={{ mb: 2 }}
          autoFocus={!isFirstTime}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />

        {/* Save as Template toggle */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isTemplate}
                onChange={(e) => setIsTemplate(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Save as Template
              </Typography>
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
            Templates are reusable starting points visible to all users
          </Typography>

          {isTemplate && (
            <Box sx={{ mt: 1.5, ml: 0 }}>
              {/* Tag chips */}
              {templateTags.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  {templateTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => setTemplateTags(templateTags.filter((t) => t !== tag))}
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: 12 }}
                    />
                  ))}
                </Stack>
              )}

              {/* Tag input */}
              <TextField
                size="small"
                fullWidth
                placeholder="Add tag... (press Enter or comma)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    const tag = tagInput.replace(',', '').trim().toLowerCase();
                    if (tag && !templateTags.includes(tag)) {
                      setTemplateTags([...templateTags, tag]);
                    }
                    setTagInput('');
                  }
                }}
                sx={{ mb: 1, '& input': { fontSize: 13 } }}
              />

              {/* Suggested tags */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Suggested tags:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {[
                  'tier-0', 'tier-1', 'mission-critical',
                  'wbr', 'mbr', 'executive-dashboard',
                  'personal', 'debugging', 'weekly-review',
                ].filter((s) => !templateTags.includes(s)).map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    variant="outlined"
                    onClick={() => setTemplateTags([...templateTags, suggestion])}
                    sx={{ fontSize: 11, cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Selection Summary
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`${selectedMetrics.length} metric${selectedMetrics.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${selectedDimensions.length} dimension${selectedDimensions.length !== 1 ? 's' : ''}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`${filterCount} filter${filterCount !== 1 ? 's' : ''}`}
              size="small"
              color="default"
              variant="outlined"
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        {isLoadedReport && (
          <Button
            onClick={handleOverwrite}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!name.trim() || (isFirstTime && !username.trim())}
          >
            Overwrite
          </Button>
        )}
        <Button
          onClick={handleSaveAsNew}
          variant={isLoadedReport ? 'outlined' : 'contained'}
          startIcon={isLoadedReport ? <SaveAsIcon /> : <SaveIcon />}
          disabled={!name.trim() || (isFirstTime && !username.trim())}
        >
          {isLoadedReport ? 'Save as New' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveModal;
